import React, {useCallback, useRef, useState} from 'react';
import Dialog from 'components/Dialog';
import {useTranslation} from 'react-i18next';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {usePostApi} from 'common/hooks/usePostApi';
import {IDiplomatObject} from 'types/DiplomatObject';
import {VALUE_NAME} from 'components/dialogs/TransferListDialog';
import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {ADMINISTRATOR} from 'common/constants';
import {useIsLdapConfigured} from 'features/layout/CommonPage/panels/SFTP/hooks/useIsLdapConfigured';
import Formik from 'components/hook-form/Formik';
import {IRoleMembersSchema, RoleMembersForm, RoleMembersSchema} from './RoleMembersForm';
import {unionBy} from 'lodash';
import {IAdminRole} from 'types/diplomat/settings/adminroles/AdminRole';
import {transformAdminUsers} from 'common/diplomatObjectUtils';
import {LDAPMapPanelPassThroughProps} from 'common/panels/LDAPmapPanel';
import {useToasts} from 'features/notifications/useToasts';

type Props = {
   role: IAdminRole;
   onClose: (updated?: boolean) => void;
};
export const RoleMembersDialog = ({onClose: _onClose, role}: Props) => {
   const {t} = useTranslation();
   const {id, name, includeLdapUsers, ldapGroupFilter} = role;
   const {addToast} = useToasts();

   const updatedLdapRef = useRef<{includeLdapUsers: boolean; ldapGroupFilter: string} | null>();
   const onClose = useCallback(() => {
      _onClose(Boolean(updatedLdapRef.current));
   }, [_onClose, updatedLdapRef]);

   const [admins, _setAdmins] = useState<IDiplomatObject[]>([]);
   const setAdmins = useCallback(
      (_admins: IDiplomatAdminUser[]) =>
         _setAdmins(_admins?.map((o) => ({...o, name: o.name || o.userName}))),
      [_setAdmins]
   );
   const {request: fetchAdmins, state: adminUsers} = useGetApi<IDiplomatAdminUser[]>(
      api.adminUsers,
      {
         onSuccess: setAdmins,
      }
   );
   React.useEffect(fetchAdmins, [fetchAdmins]);

   const isLdapConfigured = useIsLdapConfigured();

   const [membersIds, setMembersIds] = useState<string[]>();
   const [ldapMembers, setLdapMembers] = useState<IDiplomatAdminUser[]>();
   const setMembers = useCallback(
      (users: IDiplomatAdminUser[]) => {
         // const updateAdmins = unionBy(adminUsers, users, (u) => u.id).map((o) => ({
         //    ...o,
         //    name: o.name || (o as IDiplomatAdminUser).userName,
         // }));
         const [admins, ldapMembers] = users.reduce(
            (prev, current, i) => {
               if (current.ldapPasswordAuthentication) {
                  prev[1].push(current);
               } else {
                  prev[0].push(current);
               }

               return prev;
            },
            [[], []] as [IDiplomatAdminUser[], IDiplomatAdminUser[]]
         );

         // _setAdmins(admins);
         setMembersIds(admins?.map((u) => u.id));
         setLdapMembers(ldapMembers);
      },
      [setMembersIds, setLdapMembers]
   );

   const {request, loading: loadingFetch} = useGetApi<IDiplomatAdminUser[]>(
      `${api.adminusersforrole}/${id}`,
      {transformData: transformAdminUsers, onSuccess: setMembers}
   );
   const {request: updateGroup, loading: loadinUpdate} = usePostApi<IDiplomatAdminUser[]>(
      api.adminrolesSetusers
   );

   React.useEffect(request, [request]);

   const requireLdapRefresh = useRef(false);
   const {request: refreshldap, loading: loadingRefresh} = useGetApi<IDiplomatAdminUser[]>(
      `${api.refreshldapAdminRoles}/${id}`,
      {transformData: transformAdminUsers, onSuccess: setMembers}
   );

   const loading = loadinUpdate || loadingFetch; // || loadingRefresh;

   const onSubmit = React.useCallback(
      (values: IRoleMembersSchema) => {
         updateGroup(
            JSON.stringify({
               roleId: role.id,
               userIds: values.value,
               includeLdapUsers: values.includeLdapUsers,
               ldapGroupFilter: values.ldapGroupFilter,
            }),
            {
               onSuccess(data) {
                  addToast(t('Admin Role {{name}} Saved', {name: role.name}), {
                     appearance: 'success',
                  });
                  setMembers(data);
                  updatedLdapRef.current = {
                     includeLdapUsers: values.includeLdapUsers,
                     ldapGroupFilter: values.ldapGroupFilter,
                  };
                  if (requireLdapRefresh.current) {
                     refreshldap();
                  }
               },
            }
         );
      },
      [updateGroup, requireLdapRefresh, setMembers, refreshldap, updatedLdapRef]
   );

   return (
      <Dialog
         maxWidth="md"
         header={t('Role Members: {{roleName}}', {roleName: name})}
         close={onClose}
         loading={loading}
      >
         <Formik
            initialValues={RoleMembersSchema.cast({
               [VALUE_NAME]: membersIds,
               ldapGroupFilter: updatedLdapRef.current?.ldapGroupFilter || ldapGroupFilter,
               includeLdapUsers: updatedLdapRef.current?.includeLdapUsers || includeLdapUsers,
            })}
            validationSchema={name === ADMINISTRATOR ? RoleMembersSchema : undefined}
            onSubmit={onSubmit}
            passThrough={
               {
                  refreshldap,
                  loading: loadingRefresh,
                  ldapMembers: ldapMembers,
                  setRefreshLdapRequired: (val: boolean) => (requireLdapRefresh.current = val),
               } as LDAPMapPanelPassThroughProps
            }
            enableReinitialize
         >
            <RoleMembersForm
               id={id}
               onClose={onClose}
               isLdapConfigured={Boolean(isLdapConfigured)}
               // refreshLdap={refreshldap}
               valuesLabel={t('Member Of')}
               optionsLabel={t('Not Members Of')}
               options={admins}
               adminUsers={adminUsers}
            />
         </Formik>
      </Dialog>
   );
};
